/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Observable, of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { ofType, StateObservable } from 'redux-observable';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { Action, ActionCreators } from './actions';
import { RootState } from '../index';
import { handleErrorInterceptor } from '../../utils/AuthUtils';

// Getting list of roles from provisioning service
const requestRolesSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedRolesSuccess.create(response);
};

const requestRolesError = (error: AjaxError): any => {
  return ActionCreators.ReceivedRolesError.create(error);
};

export const requestRolesEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestRoles.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/provisioning/roles`).pipe(
          map((response: AjaxResponse<any>): Action => requestRolesSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> => handleErrorInterceptor(error, of(requestRolesError(error))),
          ),
        ),
    ),
  );

// Request Current Role
const receivedCurrentRoleSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedCurrentRoleSuccess.create(response);
};

const receivedCurrentRoleError = (error: AjaxError): any => {
  return ActionCreators.ReceivedCurrentRoleError.create(error);
};

export const requestCurrentRoleEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestCurrentRole.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/provisioning/roles/${$action.payload}`).pipe(
          map((response: AjaxResponse<any>): Action => receivedCurrentRoleSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(receivedCurrentRoleError(error))),
          ),
        ),
    ),
  );

// Creating a role
const roleProvisioningSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.RoleProvisioningSuccess.create(response);
};

const roleProvisioningError = (error: AjaxError): any => {
  return ActionCreators.RoleProvisioningError.create(error);
};

export const provisionRoleEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.ProvisionRole.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.post('/provisioning/roles', $action.payload).pipe(
          map((response: AjaxResponse<any>): Action => roleProvisioningSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> => handleErrorInterceptor(error, of(roleProvisioningError(error))),
          ),
        ),
    ),
  );

// Delete a role
const deleteRoleSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.DeleteRoleSuccess.create(response);
};

const deleteRoleError = (error: AjaxError): any => {
  return ActionCreators.DeleteRoleError.create(error);
};

export const deleteRoleEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.DeleteRole.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.delete(`/provisioning/roles/${$action.payload}`).pipe(
          switchMap(
            (response: AjaxResponse<any>): Array<Action> => [
              deleteRoleSuccess(response),
              ActionCreators.RequestRoles.create(),
            ],
          ),
          catchError(
            (error: AjaxError): Observable<Action> => handleErrorInterceptor(error, of(deleteRoleError(error))),
          ),
        ),
    ),
  );

// Update a role
const updateRoleSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.UpdateRoleSuccess.create(response);
};

const updateRoleError = (error: AjaxError): any => {
  return ActionCreators.UpdateRoleError.create(error);
};

export const updateRoleEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.UpdateRole.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.put(`/provisioning/roles/${$action.payload.id}`).pipe(
          map((response: AjaxResponse<any>): Action => updateRoleSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> => handleErrorInterceptor(error, of(updateRoleError(error))),
          ),
        ),
    ),
  );
